import { ERentalType } from '../types';

export enum EReviewEventName {
  REVIEW_TRIP_RATED = 'Renter Trip Rated',
  RENTER_TRIP_REVIEWED = 'Renter Trip Reviewed',
  RENTER_TRIP_RECOMMENDED = 'Renter Trip Recommended',
  RENTER_REVIEW_EDITED = 'Renter Review Edited',
  REVIEW_MODAL_OPENED = 'Review Modal Opened',
  REVIEW_STEP_COMPLETED = 'Review Step Completed',
  REVIEW_STEP_ABANDONED = 'Review Step Abandoned',
}

export enum EReviewFlow {
  GUEST_TO_HOST = 'guest_to_host',
  HOST_TO_GUEST = 'host_to_guest',
}

export enum EReviewSource {
  /** @todo handoff will be implemented when key-exchange is migrated to REACT */
  HANDOFF = 'handoff',
  TRIPS = 'trips',
  /**
   * @deprecated MP-5693 collapsed dashboard-originated reviews under TRIPS
   * so RV and campground reviews from the same TripReviewCard entry land
   * in one bucket (NewReviewTemplate's default). Historical Mixpanel data
   * still segments on `renter_dash` before that change — keep the enum
   * value for backfill queries; do not emit new events with it.
   */
  RENTER_DASH = 'renter_dash',
  HOME = 'home',
  EMAIL = 'email',
}

interface IReviewEvent extends Record<string, unknown> {
  bookingID?: number;
  ratingScore?: number;
  rentalType: ERentalType;
  isCampground: boolean;
}

export interface IReviewRatedEvent extends IReviewEvent {
  source: EReviewSource;
}

export interface IReviewSubmitEvent extends IReviewEvent {
  experienceCharacterCount?: number;
  totalImagesUploaded?: number;
}

export interface IReviewRecommendEvent extends IReviewEvent {
  communicationScore?: number;
  cleanlinessScore?: number;
  mechanicalScore?: number;
  valueScore?: number;
  listingAccuracyScore?: number;
  isRecommended?: boolean;
  /** @see {IRentalTag}.slug: 'photo-friendly' | 'family-friendly' | 'vintage' ... */
  tags: string[];
}

type TReviewDetailsEvent = IReviewRecommendEvent & IReviewSubmitEvent;

export interface TReviewEditEvent extends TReviewDetailsEvent {
  totalChanges?: number;
}

/**
 * Ordered list of RV review-modal steps (guest-to-host flow).
 *
 * NOTE: This array's length also drives ReviewTemplate's TOTAL_STEPS. Adding
 * a name here without a matching step in ReviewTemplate.tsx would extend the
 * progress bar and the Continue → Submit boundary silently. The host flow
 * has its own step names (host-side ReviewModal.tsx) — keep host and renter
 * arrays separate rather than shared.
 */
export const REVIEW_STEP_NAMES = ['overall', 'sub_ratings', 'description', 'photos'] as const;

export type TReviewStepName = (typeof REVIEW_STEP_NAMES)[number];

interface IReviewFunnelBaseEvent extends Record<string, unknown> {
  flow: EReviewFlow;
  /** Required — a dropped bookingID would be a silent hole in Segment. */
  bookingID: number;
  /** Required — ReviewTemplate provides a default (TRIPS) and upgrades to
   * EMAIL when the URL carries a `?token=`, so the field is always populated
   * at emit time. Same "silent hole in Segment" rationale as bookingID. */
  source: EReviewSource;
  rentalType: ERentalType;
  isCampground: boolean;
  isNewReview: boolean;
}

export type TReviewModalOpenedEvent = IReviewFunnelBaseEvent;

export interface IReviewStepEvent extends IReviewFunnelBaseEvent {
  /** 1-based; the corresponding name is REVIEW_STEP_NAMES[stepIndex - 1].
   *
   * Semantics differ between events (dashboard owners: read this):
   * - `Review Step Completed` — the user finished step N (clicked Continue,
   *   or persisted the review for the final step).
   * - `Review Step Abandoned` — step N was on screen when the user dismissed
   *   the modal; the step itself was NOT completed.
   *
   * Drop-off should be computed as `Opened − Step Completed[n]`, not from
   * `Abandoned`, which is a lower bound (route change / tab close /
   * parent unmount don't emit it).
   *
   * Reconciliation caveats:
   * - Funnel events are gated on the modal opening against a hydrated redux
   *   slot (readiness). Sessions where the review fetch fails or the booking
   *   arrives late emit `Renter Trip Reviewed` / `Renter Trip Rated` on submit
   *   but ZERO funnel events.
   * - `Renter Trip Reviewed` / `Renter Trip Rated` only fire for NEW reviews
   *   (`isNewReview: true`). Edit submissions still emit
   *   `Review Step Completed[stepIndex=4]` with `isNewReview: false` and no
   *   submission-level event, so the inequality below is bidirectional
   *   unless filtered.
   * Filtered to `isNewReview: true`:
   * `Renter Trip Reviewed >= Review Step Completed[stepIndex=4]` — the gap is
   * the readiness-blocked cohort. */
  stepIndex: number;
  /** Matches `REVIEW_STEP_NAMES[stepIndex - 1]`. `'unknown'` is a bailout that
   * should never appear in real traffic — flag it in the dashboard as an
   * instrumentation bug rather than treating it as a real step. */
  stepName: TReviewStepName | 'unknown';
}

export const REVIEW_UPDATED_FIELD_COUNT_KEY = 'reviewUpdatedFieldCount';
